import { useRef } from "react";
import { BreadcrumbLink } from "client/utils/breadcrumb";

// the last crumb's href is excluded: it always tracks the current pathname (facets included), so it
// changes on every filter even when nothing the shopper can see has moved. The last crumb also never
// renders as a link in Fuse, so its href has no visible effect to compare
const isSameDisplay = (left: BreadcrumbLink[], right: BreadcrumbLink[]): boolean =>
  left.length === right.length &&
  left.every(
    (link, index) => link.text === right[index].text && (index === left.length - 1 || link.href === right[index].href),
  );

/**
 * Keeps the last trail that had something in it while the shopper stays in the same place.
 *
 * A facet that filters down to zero results comes back with the taxonomy stripped out, which would
 * otherwise make the breadcrumb vanish or shorten halfway through browsing. The shopper has not
 * moved, so neither should the trail. A location change is a real move and rebuilds it. The caller
 * keys the location by category when there is one, else by pathname - two category-less pages (brand
 * store, promo root) must never read as the same place, or the first page's trail survives the move.
 *
 * Within the same location, a facet apply also rebuilds `links` into a brand-new array even when the
 * crumbs read the same on screen, because the last crumb's href tracks the URL. Holding onto the old
 * reference instead of swapping in the identical-looking rebuild keeps the consuming component from
 * re-measuring itself mid-collapse.
 */
const useLastNonEmptyTrail = (links: BreadcrumbLink[], locationKey?: string): BreadcrumbLink[] => {
  const lastTrail = useRef<BreadcrumbLink[]>(links);
  const lastLocationKey = useRef(locationKey);

  if (locationKey !== lastLocationKey.current) {
    lastLocationKey.current = locationKey;
    lastTrail.current = links;
    return links;
  }

  if (links.length && !isSameDisplay(links, lastTrail.current)) {
    lastTrail.current = links;
  }

  return lastTrail.current;
};

export default useLastNonEmptyTrail;
